import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { UserRole, ProfileType, DateFilter } from '../types/commission';
import { Filter, ChevronDown, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface FilterBarProps {
  role: UserRole;
  selectedProfiles: ProfileType[];
  onProfileChange: (profiles: ProfileType[]) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (filter: DateFilter) => void;
}
export function FilterBar({
  role,
  selectedProfiles,
  onProfileChange,
  dateFilter,
  onDateFilterChange
}: FilterBarProps) {
  const isAdmin = role === 'System Administrator';
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0
  });
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Calculate dropdown position when opening
  useEffect(() => {
    if (isProfileOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 6,
        left: rect.left
      });
    }
  }, [isProfileOpen]);
  const handleProfileToggle = (profile: ProfileType) => {
    if (selectedProfiles.includes(profile)) {
      onProfileChange(selectedProfiles.filter(p => p !== profile));
    } else {
      onProfileChange([...selectedProfiles, profile]);
    }
  };
  const clearFilters = () => {
    onProfileChange([]);
    onDateFilterChange('All');
  };
  const dateFilters: DateFilter[] = ['This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'YTD', 'All'];
  const profileTypes: ProfileType[] = ['Loan Officer', 'Sales Team Leader', 'Division Leader'];
  const hasActiveFilters = selectedProfiles.length > 0 && selectedProfiles.length < profileTypes.length || dateFilter !== 'All';
  const showChips = isAdmin && selectedProfiles.length > 0 && selectedProfiles.length < profileTypes.length;
  // Portal-based dropdown
  const dropdownMenu = isProfileOpen ? ReactDOM.createPortal(<motion.div ref={dropdownRef} initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 4
  }} transition={{
    duration: 0.15
  }} className="fixed w-56 bg-navy-800 rounded-lg shadow-2xl border border-white/10 py-1.5" style={{
    top: dropdownPos.top,
    left: dropdownPos.left,
    zIndex: 9999
  }} data-id="element-382">
          <div className="px-3 py-1.5 border-b border-white/5 mb-1" data-id="element-383">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-384">
              Filter by Profile
            </span>
          </div>
          {profileTypes.map(profile => <label key={profile} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer transition-colors" data-id="element-385">
              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedProfiles.includes(profile) ? 'bg-accent-400 border-accent-400' : 'border-slate-600 bg-transparent'}`} data-id="element-386">
                {selectedProfiles.includes(profile) && <Check size={10} className="text-navy-950" data-id="element-387" />}
              </div>
              <input type="checkbox" className="hidden" checked={selectedProfiles.includes(profile)} onChange={() => handleProfileToggle(profile)} data-id="element-388" />
              <span className="text-sm text-slate-300" data-id="element-389">{profile}</span>
            </label>)}
        </motion.div>, document.body) : null;
  return <div className="w-full bg-navy-900/60 backdrop-blur-sm border-b border-white/5 px-8 py-3 shrink-0 relative z-20" data-id="element-390">
      {/* Main filter row */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-391">
        <div className="flex flex-wrap items-center gap-3" data-id="element-392">
          {/* Profile filter dropdown */}
          {isAdmin ? <button ref={buttonRef} onClick={() => setIsProfileOpen(!isProfileOpen)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${isProfileOpen || selectedProfiles.length > 0 && selectedProfiles.length < profileTypes.length ? 'bg-accent-400/10 border-accent-400/20 text-accent-400' : 'bg-navy-800/60 border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-300'}`} data-id="element-393">
              <Filter size={14} data-id="element-394" />
              <span data-id="element-395">
                {selectedProfiles.length === 0 || selectedProfiles.length === profileTypes.length ? 'All Profiles' : `${selectedProfiles.length} Selected`}
              </span>
              <ChevronDown size={12} className={`transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} data-id="element-396" />
            </button> : <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-navy-800/60 border border-white/10 text-slate-400 cursor-default text-sm font-medium" data-id="element-397">
              <Filter size={14} data-id="element-398" />
              <span data-id="element-399">Viewing: Your Profile</span>
            </div>}

          <div className="h-5 w-px bg-white/10 hidden sm:block" data-id="element-400" />

          {/* Date filter pills */}
          <div className="bg-navy-800/60 p-1 rounded-lg flex items-center overflow-x-auto no-scrollbar border border-white/5" data-id="element-401">
            {dateFilters.map(filter => <button key={filter} onClick={() => onDateFilterChange(filter)} className={`relative px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${dateFilter === filter ? 'text-accent-400' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-402">
                {dateFilter === filter && <motion.div layoutId="activeDatePill" className="absolute inset-0 bg-navy-700/80 shadow-sm rounded-md border border-accent-400/20" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-403" />}
                <span className="relative z-10" data-id="element-404">{filter}</span>
              </button>)}
          </div>
        </div>

        {/* Clear filters */}
        {hasActiveFilters && isAdmin && <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-200 font-medium transition-colors whitespace-nowrap" data-id="element-405">
            Clear filters
          </button>}
      </div>

      {/* Active filter chips */}
      {showChips && <div className="flex flex-wrap gap-1.5 pt-2.5" data-id="element-406">
          {selectedProfiles.map(profile => <span key={profile} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-400/10 text-accent-400 border border-accent-400/20" data-id="element-407">
              {profile}
              <button onClick={() => handleProfileToggle(profile)} className="hover:bg-accent-400/20 rounded-full p-0.5 transition-colors" data-id="element-408">
                <X size={10} data-id="element-409" />
              </button>
            </span>)}
        </div>}

      {/* Portal dropdown */}
      {dropdownMenu}
    </div>;
}